/**
 * Citations, `[@karis2013real, slide 24]`, read the way the server reads them.
 *
 * The syntax is the server's (`server/app/library/cite.py`), Pandoc's subset,
 * and the place in a work it names is a locator (`server/app/library/locators.py`).
 * The server already draws every citation in kit HTML as a link; this is the
 * half the client needs to do the same in Markdown: find the brackets in a
 * run of prose, and turn each locator into the slug its address carries. The
 * two must agree, or a bracket in a claim and the same bracket in a digest
 * would open the work at different places, so the patterns below are the
 * server's, written again, and `citations.test.js` holds them to its cases.
 *
 * Imports nothing, so the Markdown renderer can take it to the public project
 * page as well, and node can test it.
 */

/* --- locators --------------------------------------------------------------- */

const DASH = String.raw`\s*[-–—]\s*`;
const CLOCK = String.raw`\d{1,2}(?::\d{2}){1,2}`;

const PAGE = new RegExp(String.raw`^(?:pp?\.|pages?)\s*(\d{1,5})(?:${DASH}(\d{1,5}))?$`, "i");
const SLIDE = new RegExp(String.raw`^slides?\s*(\d{1,5})(?:${DASH}(\d{1,5}))?$`, "i");
const NOTE = /^notes?\s+(\d{1,5})$/i;
const TIME = new RegExp(String.raw`^t\s*=\s*(${CLOCK})(?:${DASH}(${CLOCK}))?$`, "i");
const SECTION = /^(?:§\s*|sec\.?\s*|section\s+)(\d{1,3}(?:\.\d{1,3}){0,3})$/i;
const NUMBERED =
  /^(eqs?\.?|eqn\.?|equations?|figs?\.?|figures?|tables?|tab\.|listings?|lst\.?)\s*\(?(\d{1,4}(?:\.\d{1,3})?)\)?$/i;
const SLUG = /^(?:([a-z]+)\.)?(sec|eq|fig|tab|lst|p|s|n|t)([0-9][0-9.]*)(?:-([0-9]+))?$/;

/* A resource named in front of a locator, `notes p. 7`. */
const ROLES = ["slides", "notes", "paper", "chapter", "page", "transcript", "supplement"];

const PREFIX = {
  page: "p",
  slide: "s",
  note: "n",
  time: "t",
  section: "sec",
  equation: "eq",
  figure: "fig",
  table: "tab",
  listing: "lst",
};
const KIND_OF = Object.fromEntries(Object.entries(PREFIX).map(([kind, prefix]) => [prefix, kind]));

const squash = (text) => String(text ?? "").trim().split(/\s+/).filter(Boolean).join(" ");

/** Seconds as a reader writes a time: `14:05`, `1:02:03`. */
export function clock(seconds) {
  const total = Math.max(0, Math.trunc(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`;
}

function seconds(value) {
  return value.split(":").reduce((total, part) => total * 60 + Number(part), 0);
}

const locator = (kind, start, end = "", role = "") => ({ kind, start, end: end || "", role });

/* One locator with no role in front of it, or null. */
function one(text, role = "") {
  const value = squash(text);
  let found;
  if ((found = PAGE.exec(value))) return locator("page", found[1], found[2], role);
  if ((found = SLIDE.exec(value))) return locator("slide", found[1], found[2], role);
  if ((found = NOTE.exec(value))) return locator("note", found[1], "", role);
  if ((found = TIME.exec(value))) {
    const end = found[2] ? String(seconds(found[2])) : "";
    return locator("time", String(seconds(found[1])), end, role);
  }
  if ((found = SECTION.exec(value))) return locator("section", found[1], "", role);
  if ((found = NUMBERED.exec(value))) {
    const word = found[1].toLowerCase();
    const kind = word.startsWith("eq")
      ? "equation"
      : word.startsWith("fig")
        ? "figure"
        : word.startsWith("tab")
          ? "table"
          : "listing";
    return locator(kind, found[2], "", role);
  }
  return null;
}

/**
 * A locator as written, `p. 7` or `notes p. 7`, as `{ kind, start, end, role }`;
 * null when it is not one. `notes 24` is note 24: a word is only read as a
 * resource when what follows it is a locator of its own.
 */
export function parseLocator(text) {
  const value = squash(text);
  if (!value) return null;
  const found = one(value);
  if (found) return found;
  const space = value.indexOf(" ");
  if (space === -1) return null;
  const head = value.slice(0, space).toLowerCase();
  return ROLES.includes(head) ? one(value.slice(space + 1), head) : null;
}

/** The slug an address carries: `p7`, `s12-14`, `t845`, `notes.p7`. */
export function slugOf(place) {
  if (!place) return "";
  const lead = place.role ? `${place.role}.` : "";
  return `${lead}${PREFIX[place.kind]}${place.start}${place.end ? `-${place.end}` : ""}`;
}

/** A slug read back, or null for anything that is not one. */
export function fromSlug(slug) {
  const found = SLUG.exec(String(slug ?? "").trim().toLowerCase());
  if (!found) return null;
  return locator(KIND_OF[found[2]], found[3], found[4], found[1]);
}

/** As a reader would write it: `pp. 7–9`, `slide 24`, `t=14:05`, `§3.2`. */
export function locatorText(place) {
  if (!place) return "";
  const lead = place.role ? `${place.role} ` : "";
  const span = place.end ? `${place.start}–${place.end}` : place.start;
  switch (place.kind) {
    case "page":
      return `${lead}${place.end ? "pp" : "p"}. ${span}`;
    case "slide":
      return `${lead}slide${place.end ? "s" : ""} ${span}`;
    case "note":
      return `${lead}note ${place.start}`;
    case "time":
      return `${lead}t=${clock(place.start)}${place.end ? `–${clock(place.end)}` : ""}`;
    case "section":
      return `${lead}§${place.start}`;
    default: {
      const word = { equation: "eq.", figure: "fig.", table: "table", listing: "listing" }[place.kind];
      return `${lead}${word} ${place.start}`;
    }
  }
}

/** The slug of the first place in a comma-separated run, `eq. 7, p. 5`, as
 *  the server's links use it; empty when none of them is a locator. */
export function locatorSlug(text) {
  for (const part of String(text ?? "").split(",")) {
    const found = parseLocator(part);
    if (found) return slugOf(found);
  }
  return "";
}

/* --- citations -------------------------------------------------------------- */

export const KEY = /^[a-z][a-z0-9-]{1,79}$/;

/* A bracket holding one or more `@key` items, each with an optional locator. */
const GROUP = /\[(@[a-z][^[\]\n]{0,400})\]/g;
const ITEM = /^@([a-z][a-z0-9-]{1,79})(?:\s*,\s*(.+))?$/;

/** A work's address in the app, opened at a place when there is one. */
export function workAddress(key, slug = "") {
  return `#/papers/works/${key}${slug ? `/${slug}` : ""}`;
}

/* The items of one bracket, or null when any part of it is not one: a bracket
   is a citation whole or not at all, as on the server. */
function items(inner) {
  const out = [];
  let at = 0;
  for (const part of inner.split(";")) {
    const found = ITEM.exec(part.trim());
    if (!found) return null;
    const lead = part.length - part.trimStart().length;
    const located = (found[2] || "").trim();
    out.push({
      key: found[1],
      locator: located,
      slug: located ? locatorSlug(located) : "",
      // Where the item's own words sit inside the bracket, so they can be
      // drawn as a link without touching the spacing around them.
      from: at + lead,
      to: at + lead + part.trim().length,
    });
    at += part.length + 1;
  }
  return out;
}

/** Every citation in a run of text, in order: `{ start, end, items }`, with
 *  `start` and `end` around the brackets. */
export function findCitations(text) {
  const found = [];
  for (const match of String(text ?? "").matchAll(GROUP)) {
    const cited = items(match[1]);
    if (cited) found.push({ start: match.index, end: match.index + match[0].length, items: cited });
  }
  return found;
}

/**
 * A run of text cut at its citations: plain strings, and `{ text, href }`
 * for each item, the words exactly as written. `[@a; @b, p. 2]` is the
 * bracket and the semicolon as text around two links, which is how the
 * server draws the same bracket in a digest.
 */
export function citationParts(text) {
  const value = String(text ?? "");
  const out = [];
  let cursor = 0;
  // Words run on into the words before them: a part ends only where a link
  // starts or stops, so the closing bracket and the sentence after it are one.
  const words = (until) => {
    if (until > cursor) {
      const text = value.slice(cursor, until);
      if (typeof out.at(-1) === "string") out[out.length - 1] += text;
      else out.push(text);
    }
    cursor = until;
  };
  for (const citation of findCitations(value)) {
    // One past the opening bracket, where the items' offsets count from.
    const base = citation.start + 1;
    for (const item of citation.items) {
      words(base + item.from);
      out.push({ text: value.slice(base + item.from, base + item.to), href: workAddress(item.key, item.slug) });
      cursor = base + item.to;
    }
    words(citation.end);
  }
  words(value.length);
  return out;
}
